import { ref, computed, watch } from "vue";
import { TasksService } from "@/services/v2/tasks";
import { useTask } from "@/composables/tasks/useTask";
import { useNotifications } from "@/composables/useNotifications";
import { isFishImportTask } from "@/stores/v2/sites/utils";
import {
  FishImport,
  FishImportAdjustmentReason,
  FishImportEventType,
  FishImportOperation,
  FishImportProgressData,
  FishImportTransfer,
} from "@/types/v2";

interface FishImportProgressEntryView {
  key: string;
  transferKey: string;
  transferLabel: string;
  amount: number;
  delta: number;
  operation: FishImportOperation;
  eventType: FishImportEventType;
  comment: string;
  timestamp: string;
}

export function useFishImport() {
  const { activeTask } = useTask();
  const { showTaskCompletionSuccess, showWarning, showError } = useNotifications();

  const selectedTransferIndex = ref<number>(0);
  const globalImportAmount = ref<number>(0);
  const modals = ref({
    instructions: false,
    importLog: false,
  });

  // Get current import data
  const importData = computed<FishImport | null>(() => {
    if (!activeTask.value || !isFishImportTask(activeTask.value)) return null;
    return activeTask.value.details.imports;
  });

  // Get transfers from import data
  const transfers = computed<FishImportTransfer[]>(() => {
    if (!importData.value) return [];

    const transfersData = importData.value.transfers || [];
    return transfersData.map(transfer => {
      if (!transfer.progressData) {
        transfer.progressData = [];
      }
      return transfer;
    });
  });

  // Get currently selected transfer
  const selectedTransfer = computed<FishImportTransfer | null>(() => {
    if (!transfers.value.length || transfers.value.length <= selectedTransferIndex.value) return null;
    return transfers.value[selectedTransferIndex.value];
  });

  const getEntryOperation = (entry: FishImportProgressData): FishImportOperation => {
    if (entry.operation) return entry.operation;
    return (entry.amount || 0) < 0 ? "remove" : "add";
  };

  const getEntryEventType = (entry: FishImportProgressData): FishImportEventType => {
    if (entry.eventType) return entry.eventType;
    return getEntryOperation(entry) === "remove" ? "count_correction" : "import";
  };

  const getEntryAmount = (entry: FishImportProgressData): number => {
    return Math.abs(Number(entry.amount) || 0);
  };

  const getEntryDelta = (entry: FishImportProgressData): number => {
    const amount = getEntryAmount(entry);
    return getEntryOperation(entry) === "remove" ? -amount : amount;
  };

  const getTransferLabel = (transfer: FishImportTransfer): string => {
    const tankLabel = transfer.tank?.label || transfer.tank?.name || "Unknown Tank";
    return transfer.tank?.room ? `${tankLabel} (${transfer.tank.room})` : tankLabel;
  };

  const mapProgressEntryToView = (
    transfer: FishImportTransfer,
    entry: FishImportProgressData,
    index: number
  ): FishImportProgressEntryView => {
    const amount = getEntryAmount(entry);
    const operation = getEntryOperation(entry);
    const eventType = getEntryEventType(entry);
    const delta = operation === "remove" ? -amount : amount;

    return {
      key: `${transfer.key}-${index}`,
      transferKey: transfer.key,
      transferLabel: getTransferLabel(transfer),
      amount,
      delta,
      operation,
      eventType,
      comment: entry.comment || "",
      timestamp: entry.timestamp || "",
    };
  };

  const appendProgressEntry = (
    transfer: FishImportTransfer,
    payload: {
      amount: number;
      operation: FishImportOperation;
      eventType: FishImportEventType;
      comment?: string;
    }
  ): void => {
    if (!transfer.progressData) {
      transfer.progressData = [];
    }

    transfer.progressData.push({
      amount: Math.abs(payload.amount),
      comment: payload.comment?.trim() || "",
      operation: payload.operation,
      eventType: payload.eventType,
      timestamp: new Date().toISOString(),
    });
  };

  const findTransferForRemoval = (amount: number): FishImportTransfer | null => {
    const transferWithAmounts = transfers.value.map((transfer) => ({
      transfer,
      actualAmount: getTransferActualAmount(transfer),
    }));

    transferWithAmounts.sort((a, b) => b.actualAmount - a.actualAmount);
    return transferWithAmounts.find((item) => item.actualAmount >= amount)?.transfer || null;
  };

  const getDefaultCommentByReason = (reason: FishImportAdjustmentReason): string => {
    const reasonLabels: Record<FishImportAdjustmentReason, string> = {
      mortality: "Mortality adjustment",
      quality_issue: "Quality issue adjustment",
      count_correction: "Count correction adjustment",
      other_adjustment: "Other adjustment",
    };

    return reasonLabels[reason];
  };

  // Calculate total actual amount for a specific transfer
  const getTransferActualAmount = (transfer: FishImportTransfer): number => {
    if (!transfer) return 0;

    return (transfer.progressData || []).reduce((sum, entry) => {
      return sum + getEntryDelta(entry);
    }, 0);
  };

  // Track total actual amount for all transfers
  const totalActualAmount = computed((): number => {
    return transfers.value.reduce((sum, transfer) => {
      return sum + getTransferActualAmount(transfer);
    }, 0);
  });

  // Track total target amount for all transfers
  const totalTargetAmount = computed((): number => {
    return transfers.value.reduce(
      (sum, transfer) => sum + transfer.targetAmount,
      0
    );
  });

  // Get actual amount for the currently selected transfer
  const selectedTransferActualAmount = computed((): number => {
    if (!selectedTransfer.value) return 0;
    return getTransferActualAmount(selectedTransfer.value);
  });

  const selectedTransferProgressEntries = computed<FishImportProgressEntryView[]>(() => {
    if (!selectedTransfer.value) return [];
    return getTransferProgressEntries(selectedTransfer.value);
  });

  const selectedTransferBreakdown = computed(() => {
    if (!selectedTransfer.value?.progressData) {
      return { added: 0, removed: 0, net: 0 };
    }

    const breakdown = selectedTransfer.value.progressData.reduce((acc, entry) => {
      const amount = getEntryAmount(entry);
      if (getEntryOperation(entry) === "remove") {
        acc.removed += amount;
      } else {
        acc.added += amount;
      }
      return acc;
    }, { added: 0, removed: 0 });

    return {
      ...breakdown,
      net: breakdown.added - breakdown.removed,
    };
  });

  // Select a transfer by index
  const selectTransfer = (index: number): void => {
    if (index >= 0 && index < transfers.value.length) {
      selectedTransferIndex.value = index;
    }
  };

  // Record amount for the current transfer
  const recordTransferAmount = (): void => {
    const parsedAmount = Number(globalImportAmount.value);

    if (!selectedTransfer.value || !parsedAmount) {
      if (!selectedTransfer.value) {
        showError("No Transfer Selected", "Please select a transfer before adding fish.");
      } else if (!parsedAmount) {
        showWarning("Invalid Amount", "Please enter a valid amount greater than 0.");
      }
      return;
    }

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      showWarning("Invalid Amount", "Add fish amount must be greater than 0.");
      return;
    }

    const normalizedAmount = Math.abs(parsedAmount);
    const currentAmount = getTransferActualAmount(selectedTransfer.value);
    const newAmount = currentAmount + normalizedAmount;

    // Check if the new amount exceeds the target amount significantly
    if (newAmount > selectedTransfer.value.targetAmount * 1.1) {
      showWarning("Amount Warning", `The imported amount (${newAmount}) significantly exceeds the target amount (${selectedTransfer.value.targetAmount}). Please verify this is correct.`);
    }

    appendProgressEntry(selectedTransfer.value, {
      amount: normalizedAmount,
      operation: "add",
      eventType: "import",
    });

    // Reset the input amount
    globalImportAmount.value = 0;
  };

  const recordTransferRemoval = (
    amount: number,
    reason: FishImportAdjustmentReason,
    comment: string
  ): boolean => {
    if (!selectedTransfer.value) {
      showError("No Transfer Selected", "Please select a transfer before recording a removal.");
      return false;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      showWarning("Invalid Amount", "Removal amount must be greater than 0.");
      return false;
    }

    const normalizedAmount = Math.abs(amount);
    const currentAmount = getTransferActualAmount(selectedTransfer.value);
    if (normalizedAmount > currentAmount) {
      showWarning(
        "Insufficient Imported Fish",
        `You cannot remove ${normalizedAmount} fish from this transfer because only ${currentAmount} are currently available.`
      );
      return false;
    }

    const trimmedComment = comment.trim();
    if (reason === "other_adjustment" && !trimmedComment) {
      showWarning("Comment Required", "Please add a comment when using Other removal reason.");
      return false;
    }

    appendProgressEntry(selectedTransfer.value, {
      amount: normalizedAmount,
      operation: "remove",
      eventType: reason,
      comment: trimmedComment || getDefaultCommentByReason(reason),
    });

    return true;
  };

  // Add discrepancy explanation as an explicit adjustment event.
  const addDiscrepancyOnTask = (comment: string): boolean => {
    const discrepancy = totalTargetAmount.value - totalActualAmount.value;
    if (discrepancy === 0) return true;

    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      showWarning("Comment Required", "Please provide an explanation for the discrepancy.");
      return false;
    }

    const operation: FishImportOperation = discrepancy > 0 ? "add" : "remove";
    const normalizedAmount = Math.abs(discrepancy);

    let targetTransfer: FishImportTransfer | null =
      selectedTransfer.value || transfers.value[0] || null;

    if (!targetTransfer) {
      showError("No Transfer Available", "Unable to record discrepancy adjustment without a transfer.");
      return false;
    }

    if (operation === "remove" && getTransferActualAmount(targetTransfer) < normalizedAmount) {
      const fallbackTransfer = findTransferForRemoval(normalizedAmount);
      if (fallbackTransfer) {
        targetTransfer = fallbackTransfer;
        const fallbackTransferIndex = transfers.value.findIndex(
          (transfer) => transfer.key === fallbackTransfer.key
        );
        if (fallbackTransferIndex !== -1) {
          selectedTransferIndex.value = fallbackTransferIndex;
        }
      }
    }

    if (operation === "remove" && getTransferActualAmount(targetTransfer) < normalizedAmount) {
      showError(
        "Unable to Reconcile Discrepancy",
        `Cannot remove ${normalizedAmount} fish because no transfer has enough imported fish to adjust.`
      );
      return false;
    }

    appendProgressEntry(targetTransfer, {
      amount: normalizedAmount,
      operation,
      eventType: "discrepancy_adjustment",
      comment: trimmedComment,
    });

    return true;
  };

  // Complete the import task
  const handleCompleteTask = async (): Promise<void> => {
    if (!activeTask.value) return;

    // Check if explanation is needed when amounts don't match
    let explanationExists = false;

    // Check all transfers for comments
    for (const transfer of transfers.value) {
      if (
        transfer.progressData &&
        transfer.progressData.some((entry) => {
          const hasComment = !!entry.comment && entry.comment.trim() !== "";
          const isNonImportEvent = getEntryEventType(entry) !== "import";
          return hasComment || isNonImportEvent;
        })
      ) {
        explanationExists = true;
        break;
      }
    }

    if (totalActualAmount.value !== totalTargetAmount.value && !explanationExists) {
      showWarning("Import Discrepancy", "The imported amount doesn't match the target amount. Please provide an explanation.");
      modals.value.importLog = true;
      return;
    }

    try {
      await TasksService.finishTask(activeTask.value);
      showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
      console.error('Error completing fish import task:', error);
      showError("Task Completion Failed", "Failed to complete the fish import task. Please try again.");
    }
  };

  watch(
    () => transfers.value,
    (newTransfers) => {
      if (!newTransfers.length) {
        selectedTransferIndex.value = 0;
        return;
      }

      if (selectedTransferIndex.value >= newTransfers.length) {
        selectedTransferIndex.value = 0;
      }

      newTransfers.forEach((transfer) => {
        if (!transfer.progressData) {
          transfer.progressData = [];
        }
      });
    },
    { immediate: true }
  );

  const getEventTypeLabel = (eventType: FishImportEventType): string => {
    const labels: Record<FishImportEventType, string> = {
      import: "Import",
      mortality: "Mortality",
      quality_issue: "Quality Issue",
      count_correction: "Count Correction",
      discrepancy_adjustment: "Discrepancy",
      other_adjustment: "Other",
    };
    return labels[eventType];
  };

  const getOperationLabel = (operation: FishImportOperation): string => {
    return operation === "add" ? "Added" : "Removed";
  };

  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return "N/A";
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return `${parsed.toLocaleDateString()} ${parsed.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const getTransferProgressEntries = (
    transfer: FishImportTransfer | null
  ): FishImportProgressEntryView[] => {
    if (!transfer?.progressData) return [];

    return transfer.progressData
      .map((entry, index) => mapProgressEntryToView(transfer, entry, index))
      .reverse();
  };

  const allTransferProgressEntries = computed<FishImportProgressEntryView[]>(() => {
    const allEntries: FishImportProgressEntryView[] = [];

    transfers.value.forEach((transfer) => {
      allEntries.push(...getTransferProgressEntries(transfer));
    });

    return allEntries.sort((a, b) => {
      const timestampA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timestampB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timestampB - timestampA;
    });
  });

  const hasAnyRemovalEvent = computed(() => {
    return transfers.value.some((transfer) =>
      (transfer.progressData || []).some((entry) => getEntryOperation(entry) === "remove")
    );
  });

  const totalRemovedAmount = computed(() => {
    return transfers.value.reduce((sum, transfer) => {
      return sum + (transfer.progressData || []).reduce((transferSum, entry) => {
        if (getEntryOperation(entry) === "remove") {
          return transferSum + getEntryAmount(entry);
        }
        return transferSum;
      }, 0);
    }, 0);
  });

  const totalAddedAmount = computed(() => {
    return transfers.value.reduce((sum, transfer) => {
      return sum + (transfer.progressData || []).reduce((transferSum, entry) => {
        if (getEntryOperation(entry) === "add") {
          return transferSum + getEntryAmount(entry);
        }
        return transferSum;
      }, 0);
    }, 0);
  });

  const hasSelectedTransferEvents = computed(() => {
    return selectedTransferProgressEntries.value.length > 0;
  });

  return {
    importData,
    transfers,
    selectedTransfer,
    selectedTransferIndex,
    selectTransfer,
    globalImportAmount,
    recordTransferAmount,
    recordTransferRemoval,
    addDiscrepancyOnTask,
    getTransferActualAmount,
    getTransferProgressEntries,
    allTransferProgressEntries,
    totalActualAmount,
    totalTargetAmount,
    totalAddedAmount,
    totalRemovedAmount,
    selectedTransferActualAmount,
    selectedTransferBreakdown,
    selectedTransferProgressEntries,
    hasSelectedTransferEvents,
    hasAnyRemovalEvent,
    getEventTypeLabel,
    getOperationLabel,
    formatTimestamp,
    handleCompleteTask,
    instructionsModal: computed(() => modals.value.instructions),
    toggleInstructionsModal: () => (modals.value.instructions = !modals.value.instructions),
    importLogModal: computed(() => modals.value.importLog),
    toggleImportLogModal: () => (modals.value.importLog = !modals.value.importLog),
  };
}
